import React, { useState } from 'react';
import { ChevronDown, ChevronUp, CheckCircle, XCircle, MinusCircle } from 'lucide-react';
import {
  EvaluationDetail,
  ParsedArtifact,
  VoiceCallEvaluationMetrics,
  VoiceCallFailureReasons,
} from 'api-types';
import { ComprehensiveEvaluationView } from './ComprehensiveEvaluationView';
import { SpinnerLoader } from '../../../components/Loader/SpinnerLoader';

interface DeveloperEvaluationSectionProps {
  evaluation: EvaluationDetail | null;
  loading?: boolean;
  error?: string | null;
}

function MetricChip({
  label,
  pass,
  neutral,
}: {
  label: string;
  pass: boolean;
  neutral?: boolean;
}) {
  if (neutral) {
    return (
      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 text-gray-400 text-sm">
        <MinusCircle className="h-3.5 w-3.5 flex-shrink-0" />
        <span>{label}</span>
      </div>
    );
  }
  return (
    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm ${pass ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
      {pass ? (
        <CheckCircle className="h-3.5 w-3.5 flex-shrink-0" />
      ) : (
        <XCircle className="h-3.5 w-3.5 flex-shrink-0" />
      )}
      <span>{label}</span>
    </div>
  );
}

function captureLabel(
  value: string | undefined,
  field: 'Name' | 'Phone'
): { label: string; pass: boolean; neutral: boolean } {
  if (value === 'captured') return { label: `${field} Captured`, pass: true, neutral: false };
  if (value === 'not_reached') return { label: `${field} — Not Reached`, pass: true, neutral: true };
  return { label: `${field} Not Captured`, pass: false, neutral: false };
}

function MetricsCard({
  metrics,
  failureReasons,
}: {
  metrics: VoiceCallEvaluationMetrics;
  failureReasons?: VoiceCallFailureReasons;
}) {
  const nameInfo = captureLabel(metrics.nameCaptured, 'Name');
  const phoneInfo = captureLabel(metrics.phoneCaptured, 'Phone');

  const failures = [
    { key: 'routingCorrect', reason: failureReasons?.routingCorrect },
    { key: 'allQuestionsAsked', reason: failureReasons?.allQuestionsAsked },
    { key: 'humanRequested', reason: failureReasons?.humanRequested },
    { key: 'frustrated', reason: failureReasons?.frustrated },
  ].filter(f => f.reason);

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Evaluation Metrics</h3>
      <div className="flex flex-wrap gap-2">
        <MetricChip label="Routing Correct" pass={metrics.routingCorrect ?? true} />
        <MetricChip label="All Questions Asked" pass={metrics.allQuestionsAsked ?? true} />
        <MetricChip label="Human Not Requested" pass={!metrics.humanRequested} />
        <MetricChip label="User Not Frustrated" pass={!metrics.frustrated} />
        <MetricChip label={nameInfo.label} pass={nameInfo.pass} neutral={nameInfo.neutral} />
        <MetricChip label={phoneInfo.label} pass={phoneInfo.pass} neutral={phoneInfo.neutral} />
      </div>
      {failures.length > 0 && (
        <div className="mt-3 space-y-2 border-t border-gray-200 pt-3">
          {failures.map(f => (
            <p key={f.key} className="text-sm text-gray-700">{f.reason}</p>
          ))}
        </div>
      )}
    </div>
  );
}

export const DeveloperEvaluationSection: React.FC<DeveloperEvaluationSectionProps> = ({
  evaluation,
  loading = false,
  error = null,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Evaluation</h2>
        <div className="flex justify-center items-center py-8">
          <SpinnerLoader />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Evaluation</h2>
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (!evaluation) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Evaluation</h2>
        <p className="text-gray-500">No evaluation data available for this call.</p>
      </div>
    );
  }

  const parsedArtifact = evaluation.parsedArtifact as ParsedArtifact | undefined;
  const metrics = evaluation.evaluationResult?.metrics;
  const failureReasons = evaluation.evaluationResult?.failureReasons;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm">
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h2 className="text-xl font-semibold text-gray-900">Evaluation</h2>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5 text-gray-500" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-500" />
        )}
      </div>

      {isExpanded && (
        <div className="mt-6 space-y-4">
          {parsedArtifact ? (
            <ComprehensiveEvaluationView parsedArtifact={parsedArtifact} />
          ) : (
            <p className="text-gray-500">No call flow data available.</p>
          )}
          {metrics && <MetricsCard metrics={metrics} failureReasons={failureReasons} />}
        </div>
      )}
    </div>
  );
};
